/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'

import {
  EuiButton,
  EuiPopover,
  EuiPopoverProps,
  EuiPopoverFooter,
  EuiPopoverTitle,
  EuiIcon,
  EuiFlexGroup,
  EuiFlexItem,
  IconType,
  EuiText,
  EuiTextTruncate,
  useEuiTheme,
} from '@elastic/eui'

import CopyButton from '@modules/cui/CopyButton'

export type EndpointsPopoverItem = {
  title: string
  value: string
}

export type EndpointsOpenButtonProps = {
  title: string | React.ReactNode
  url: string
}

interface EndpointsPopoverProps {
  iconType: IconType
  title: string
  isOpen: boolean
  closePopover: () => void
  anchorButton: EuiPopoverProps['button']
  openButtonProps?: EndpointsOpenButtonProps
  popoverItems: EndpointsPopoverItem[]
}

export const EndpointsPopover = (props: EndpointsPopoverProps) => {
  const { iconType, title, anchorButton, isOpen, openButtonProps, popoverItems, closePopover } =
    props

  const { euiTheme } = useEuiTheme()

  return (
    <EuiPopover button={anchorButton} isOpen={isOpen} closePopover={closePopover} hasArrow={true}>
      <EuiPopoverTitle data-test-id='endpoints-popover.title'>
        <EuiFlexGroup direction='row' gutterSize='s' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiIcon type={iconType} />
          </EuiFlexItem>
          <EuiFlexItem>{title}</EuiFlexItem>
        </EuiFlexGroup>
      </EuiPopoverTitle>

      <EuiFlexGroup direction='column' style={{ width: 300 }}>
        {popoverItems.map((item, index) => (
          <EuiFlexItem
            data-test-id={`endpoints-popover.item-${index}`}
            key={item.title}
            style={{
              borderBottom: index === popoverItems.length - 1 ? undefined : euiTheme.border.thin,
              paddingBottom: index === popoverItems.length - 1 ? undefined : euiTheme.size.m,
            }}
          >
            <EuiFlexGroup direction='column' gutterSize='s'>
              <EuiFlexItem data-test-id={`endpoints-popover.item-title-${index}`}>
                <EuiText size='s'>{item.title}</EuiText>
              </EuiFlexItem>
              <EuiFlexItem data-test-id={`endpoints-popover.item-value-${index}`}>
                <EuiFlexGroup direction='row' alignItems='center'>
                  <EuiFlexItem>
                    <EuiText size='s' color='subdued'>
                      <EuiTextTruncate text={item.value} />
                    </EuiText>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <CopyButton
                      data-test-id={`endpoints-popover.copy-button-${index}`}
                      value={item.value}
                      color='text'
                      display='base'
                      size='xs'
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>

      {openButtonProps && (
        <EuiPopoverFooter>
          <EuiButton
            data-test-id='endpoints-popover.open-button'
            fullWidth={true}
            color='text'
            href={openButtonProps.url}
            target='_blank'
          >
            {openButtonProps.title}
          </EuiButton>
        </EuiPopoverFooter>
      )}
    </EuiPopover>
  )
}
